// buildStationsOptions.ts
import { EChartsOption } from "echarts";
import { RetroChart, flattenSegments } from "../../../models/chartData";

interface BuildStationsOptionsParams {
  chart: RetroChart;
  labels: Record<string, string>;
  activeSeries: Record<string, boolean>;
  dynamicLevel: number;
  colors: Record<string, string>;
  maxDate?: number;
  zoomStart?: number;
  zoomEnd?: number;
  limitValue?: number;
}

const MONTHS = [
  "янв", "фев", "мар", "апр", "май", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

const STATION_LIMIT = 32;

/** Извлекает номер станции из имени серии: "Station 4" → "4", "4" → "4" */
const stationNumber = (name: string): string => {
  const m = name.match(/(\d+)$/);
  return m ? m[1] : name;
};

export const buildStationsOptions = ({
  chart,
  labels,
  activeSeries,
  dynamicLevel,
  colors,
  maxDate,
  zoomStart = 0,
  zoomEnd = 100,
  limitValue = STATION_LIMIT,
}: BuildStationsOptionsParams): EChartsOption => {
  // Серии станций приходят в unified-формате. МДБН: имена — номера станций ("1".."15").
  // СМБ: имена — "Station 1".."Station 15". Нормализуем в номер и сортируем по нему.
  const stationIds = chart.series
    .map((s) => s.name)
    .map((name) => ({ name, num: stationNumber(name) }))
    .filter(({ num }) => labels[num] !== undefined)
    .sort((a, b) => Number(a.num) - Number(b.num))
    .map(({ num }) => num);

  const series: any[] = stationIds.map((stationId) => {
    const found = chart.series.find(
      (s) => stationNumber(s.name) === stationId,
    );
    const { points } = flattenSegments(found?.segments ?? []);
    return {
      name: `${labels[stationId]}-(${stationId})`,
      type: "line",
      data: points,
      smooth: false,
      symbol: "circle",
      symbolSize: 3,
      sampling: "none",
      large: false,
      largeThreshold: 2000,
      lineStyle: { width: 1.6 },
      color: colors[stationId],
      connectNulls: false,
    };
  });

  const limitData = stationIds.length
    ? flattenSegments(
        chart.series.find(
          (s) => stationNumber(s.name) === stationIds[0],
        )?.segments ?? [],
      ).points
        .filter((p): p is [number, number] => p[1] !== null)
        .map(([ts]) => [ts, limitValue] as [number, number])
    : [];

  series.push({
    name: "Лимит",
    type: "line",
    data: limitData,
    step: "end",
    symbol: "none",
    lineStyle: { color: "#d62728", width: 1, type: "dashed" },
    tooltip: { show: false },
    animation: false,
  });

  const tooltipFormatter = (params: any[]) => {
    const first = params[0];
    let timestamp = first?.axisValue || first?.value?.[0];
    if (!timestamp) return "Нет данных";

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Некорректное время";

    const pad = (num: number) => num.toString().padStart(2, "0");
    let result = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}<br/>`;

    params.forEach((item) => {
      if (Array.isArray(item.value) && typeof item.value[1] === "number") {
        result += `${item.marker} ${item.seriesName}: ${item.value[1]} А<br/>`;
      }
    });

    result += `<span style="display:inline-block;margin-right:5px;width:10px;height:10px;border-radius:50%;background:#d62728"></span>Лимит: ${limitValue} А<br/>`;

    return result;
  };

  const legendSelected = stationIds.reduce(
    (acc, id) => {
      acc[`${labels[id]}-(${id})`] = activeSeries[id] ?? true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const stationNames = stationIds.map((id) => `${labels[id]}-(${id})`);

  const getGroupIndex = (stationId: string): number => {
    const addr = Number(stationId);
    if (addr >= 1 && addr <= 5) return 0;
    if (addr >= 6 && addr <= 10) return 1;
    if (addr >= 11 && addr <= 15) return 2;
    return 0;
  };

  const groups: { names: string[]; selected: Record<string, boolean> }[] = [
    { names: [], selected: {} },
    { names: [], selected: {} },
    { names: [], selected: {} },
  ];

  stationNames.forEach((name) => {
    const match = name.match(/\((\d+)\)$/);
    if (match) {
      const addr = match[1];
      const groupIdx = getGroupIndex(addr);
      groups[groupIdx].names.push(name);
      groups[groupIdx].selected[name] = legendSelected[name];
    }
  });

  const nonEmptyGroups = groups.filter((g) => g.names.length > 0);

  let legend: any;
  if (nonEmptyGroups.length === 0) {
    legend = { data: [], selected: {}, top: 10, textStyle: { color: "#333", fontSize: 12 } };
  } else if (nonEmptyGroups.length === 1) {
    legend = {
      data: nonEmptyGroups[0].names,
      selected: nonEmptyGroups[0].selected,
      top: 10,
      textStyle: { color: "#333", fontSize: 12 },
    };
  } else {
    const legends: any[] = [];
    const totalWidth = 100;
    const gap = 3;
    const groupWidth = (totalWidth - gap * (nonEmptyGroups.length - 1)) / nonEmptyGroups.length;

    nonEmptyGroups.forEach((group, i) => {
      const leftPos = i * (groupWidth + gap);
      legends.push({
        data: group.names,
        selected: group.selected,
        top: 8,
        left: `${leftPos + 2}%`,
        orient: "horizontal",
        width: `${groupWidth}%`,
        itemWidth: 14,
        itemHeight: 10,
        itemGap: 8,
        textStyle: { color: "#333", fontSize: 11.5 },
        padding: [0, 5],
      });
    });
    legend = legends;
  }

  return {
    backgroundColor: "white",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        snap: true,
        label: { precision: 0, backgroundColor: "#1894c3" },
      },
      confine: false,
      appendToBody: true,
      formatter: tooltipFormatter,
    },
    legend: Array.isArray(legend) ? legend : [legend],
    grid: {
      left: 15,
      right: "1%",
      bottom: 25,
      top: Array.isArray(legend) ? 78 : 50,
      containLabel: true,
    },
    xAxis: {
      type: "time",
      min: (() => {
        if (!chart.series.length) return undefined;
        let min = Infinity;
        chart.series.forEach((s) => {
          const { points } = flattenSegments(s.segments);
          points.forEach((p) => {
            if (p[1] !== null && p[0] < min) min = p[0];
          });
        });
        return min === Infinity ? undefined : min;
      })(),
      max: maxDate,
      axisLabel: {
        color: "#555",
        fontSize: 10,
        formatter: (value: number) => {
          const date = new Date(value);
          return `${date.getDate()} ${MONTHS[date.getMonth()]}\n${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
        },
      },
      axisLine: { lineStyle: { color: "#ccc" } },
      splitLine: { lineStyle: { color: "#eee" } },
    },
    yAxis: {
      type: "value",
      name: "Ток, А",
      nameTextStyle: {
        color: "#333",
        fontSize: 11,
        fontWeight: "bold",
        align: "left",
        verticalAlign: "top",
        padding: [-10, 0, 0, -30],
      },
      axisLabel: { color: "#555" },
      axisLine: { lineStyle: { color: "#ccc" } },
      splitLine: { lineStyle: { color: "#eee" } },
      min: 0,
      max: dynamicLevel,
      interval: dynamicLevel / 4,
    },
    dataZoom: [
      {
        type: "inside",
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        start: zoomStart,
        end: zoomEnd,
        minSpan: 5,
        throttle: 16,
        zoomLock: false,
        filterMode: "none",
      },
    ],
    series,
  };
};
